<template>
  <div class="page-editor-properties-toolbar">
    <div v-if="dataObject">
      <template v-if="authorize(permissibleActions.nameable.minPermissionLevel)">
        <p><strong>UUID:</strong> {{ dataObject.uuid }}</p>
      </template>

      <div class="form-group" v-if="authorize(permissibleActions.nameable.minPermissionLevel)">
        <label :for="dataObject.name">Name</label>
        <input
          type="text"
          class="form-control"
          :disabled='isDisabled'
          v-model="dataObject.name"
          :placeholder="dataObject.name"
          :id="dataObject.name"
          @change="updateProperties"
        />
      </div>
      <div class="form-group" v-else>
        <label :for="dataObject.name">Name</label>
        <input
          type="text"
          class="form-control"
          :disabled='true'
          v-model="dataObject.name"
          :placeholder="dataObject.name"
          :id="dataObject.name"
          @change="updateProperties"
        />
      </div>

      <template v-if="authorize(permissibleActions.taggable.minPermissionLevel)">
        <tags-input
            ref="tagInput"
            element-id="tags"
            v-model="tags"
            :typeahead="true"
            :add-tags-on-comma="true"
            :existing-tags="existingTags"
            @tag-added="onTagAdded"
            @tag-removed="onTagRemoved"
        ></tags-input>
      </template>
      <template v-else>
        <tags-input
            ref="tagInput"
            element-id="tags"
            v-model="tags"
            :disabled='true'
            :typeahead="true"
            :add-tags-on-comma="true"
            :existing-tags="existingTags"
            @tag-added="onTagAdded"
            @tag-removed="onTagRemoved"
        ></tags-input>
      </template>

      <hr/>
      <div v-if="dataObject.object_model_name !== 'Page'">

        <b-dropdown text="Actions">
          <template v-if="authorize(permissibleActions.repositionable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' @click="expandToFitParentAction(dataObject)"><font-awesome-icon icon="expand-arrows-alt" /> - Expand To Fit Parent</b-dropdown-item>
            <b-dropdown-item :disabled='isDisabled' @click="expandToFitParentHeightAction(dataObject)"><font-awesome-icon icon="arrows-alt-v" /> - Expand To Fit Parent Height</b-dropdown-item>
            <b-dropdown-item :disabled='isDisabled' @click="expandToFitParentWidthAction(dataObject)"><font-awesome-icon icon="arrows-alt-h" /> - Expand To Fit Parent Width</b-dropdown-item>
            <b-dropdown-item :disabled='isDisabled' @click="verticallyCenterAction(dataObject)">Vertically Center to Parent</b-dropdown-item>
            <b-dropdown-item :disabled='isDisabled' @click="horizontallyCenterAction(dataObject)">Horizontally Center to Parent</b-dropdown-item>
            <b-dropdown-item :disabled='isDisabled' @click="centerAction(dataObject)"><font-awesome-icon icon="compress-arrows-alt" /> - Center to Parent</b-dropdown-item>
            <b-dropdown-divider />
          </template>

          <template v-if="authorize(permissibleActions.cloneable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' @click="cloneAction(dataObject)"><font-awesome-icon icon="clone" /> - Clone</b-dropdown-item >
          </template>

          <template v-if="authorize(permissibleActions.removable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' variant="danger" @click="removeAction(dataObject)"><font-awesome-icon icon="trash" /> - Remove</b-dropdown-item >
          </template>

          <template v-if="authorize(permissibleActions.templatable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' variant="danger" @click="duplicateToTemplate(dataObject)">Templatize</b-dropdown-item >
          </template>

          <template v-if="authorize(permissibleActions.nestable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' variant="success" @click="addChildElementAction(dataObject)"><font-awesome-icon icon="file" /> - Add Child Element</b-dropdown-item >
          </template>

          <template v-if="authorize(permissibleActions.nestable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' variant="info" @click="displayAddChildModal(dataObject)"><font-awesome-icon icon="folder" /> - Add Child Component</b-dropdown-item >
          </template>

          <template v-if="authorize(permissibleActions.remappable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' variant="danger" @click="showRemapModal()">Remap</b-dropdown-item >
          </template>

          <template v-if="authorize(permissibleActions.remappable.minPermissionLevel)">
            <b-dropdown-item :disabled='isDisabled' @click="lockOrUnlockChildren()"><font-awesome-icon :icon="dataObject.properties.locked ? 'unlock' : 'lock'" /> - {{ dataObject.properties.locked ? "Super Unlock" : "Super Lock" }}</b-dropdown-item >
          </template>
        </b-dropdown>

        <hr/>
      </div>

      <div v-if="dataObject.properties">

        <h6 :data-property-version="dataObject.properties.property_version" :data-version="dataObject.properties.version" :uuid="dataObject.uuid">Properties</h6>

        <div v-if="optionsSheet.prop">

          <div v-if="optionsSheet.prop[elementType]">
            <div v-for="value in optionsSheet.prop[elementType]['allowed_props']" :key="value">

              <!--              <div :data-permission-level="optionsSheet.options[value]['minPermissionLevel']">-->
              <!--                <pre>-->
              <!--                  {{ optionsSheet.options[value] }}-->
              <!--                </pre>-->
              <!--              </div>-->

              <div v-if="optionsSheet.options[value] && isDisabled === false" class="tool-tip-relative">

                <div v-if="optionsSheet.options[value]['type'] === 'select-list' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <label :for="value">Select Items</label>
                  <tags-input element-id="select-list"
                              :id="value"
                              v-model="dataObject.properties['select_list_items']"
                              :typeahead="true"
                              :existing-select-items="existingSelectItems"
                              :add-tags-on-comma="true"
                              placeholder="Add Select Items"
                              @tag-added="onSelectItemAdded"
                              @tag-removed="onSelectItemRemoved"
                  ></tags-input>
                </div>

                <div v-if="optionsSheet.options[value]['type'] === 'correct_answer' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div class="form-group">
                    <label :for="value">Correct Answer</label>
                    <select v-model="correctAnswer" :name="dataObject.name" :id="dataObject.id" @change="onCorrectAnswerSelection">
                      <option value="">Choose</option>
                      <option v-for="(option, index) in dataObject.parent_elements" :key="index" :value="option.uuid | stripHtml">{{ option.properties.label | stripHtml }}</option>
                    </select>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'keyboard_select' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div class="form-group">
                    <p>SELECT KEYBOARD</p>
                    <select v-model="dataObject.properties[value]" :name="dataObject.name" :id="dataObject.id" @change="keyboardSelection">
                      <option value="">Choose</option>
                      <option v-for="(option, index) in keyboards" :key="index" :value="option.value.uuid | stripHtml">{{ option.text | stripHtml }}</option>
                    </select>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'image_placeholder_select' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div class="form-group">
                    <label :for="value">{{ value }}</label>
                    <b-form-select
                      :id="value"
                      v-model="dataObject.properties[value]"
                      :options="imagePlaceholderOptions"
                      size="sm"
                      class="mt-3"
                      @change="updateProperties"
                    >
                    </b-form-select>
                  </div>
                </div>

                <div v-if="optionsSheet.options[value]['type'] === 'correct_answer' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  helper_text
                </div>

                <div v-if="optionsSheet.options[value]['type'] === 'number' && !optionsSheet.options[value]['custom'] && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <input
                        type="number"
                        step="0.01"
                        :min="optionsSheet.options[value]['min']"
                        :max="optionsSheet.options[value]['max']"
                        class="form-control"
                        v-model.number="dataObject.properties[value]"
                        :placeholder="dataObject.properties[value]"
                        :id="value"
                        @change="updateProperties"
                      />
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'string' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div class="form-group">
                    <label :for="value">{{value}}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="dataObject.properties[value]"
                      :placeholder="dataObject.properties[value]"
                      :id="value"
                      @change="updateProperties"
                    />
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'text' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <ckeditor
                        :editor="editor"
                        v-model="dataObject.properties[value]"
                        :config="editorConfig"
                        @blur="updateProperties"
                      />
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'boolean' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <input
                        type="checkbox"
                        class="form-control"
                        v-model="dataObject.properties[value]"
                        :id="value"
                        :true-value="true"
                        :false-value="false"
                        :checked="dataObject.properties[value]"
                        @change="updateProperties"
                      />
                      <div v-if="dataObject.properties.custom_style_toggle && optionsSheet.options[value]['subtype'] === 'custom_block'">
                        <div class="form-group custom-styles">
                          <b-button variant="info" @click="showCustomStylesModal()">Custom Styles</b-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'select' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <b-form-select
                        :id="value"
                        v-model="dataObject.properties[value]"
                        :options="optionsSheet.options[value]['allowed_values']"
                        size="sm"
                        class="mt-3"
                        @change="updateProperties"
                      ></b-form-select>
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'sound_effect' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <b-form-select
                        :id="value"
                        v-model="dataObject.properties[value]"
                        :options="allSounds"
                        size="sm"
                        class="mt-3"
                        @change="updateProperties"
                      ></b-form-select>
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'slider' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <input
                        type="range"
                        step="0.01"
                        :min="optionsSheet.options[value]['min']"
                        :max="optionsSheet.options[value]['max']"
                        class="form-control"
                        v-model="dataObject.properties[value]"
                        :placeholder="dataObject.properties[value]"
                        :id="value"
                        @change="updateProperties"
                      />
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'colour' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <input
                        type="color"
                        class="form-control"
                        v-model="dataObject.properties[value]"
                        :placeholder="dataObject.properties[value]"
                        :id="value"
                        @change="updateProperties"
                      />
                    </div>
                    <hr/>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'file' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="dataObject.asset_name">
                    <p><b>Current Asset:</b> {{dataObject.asset_name}}</p>
                  </div>
                  <div v-if="ifPropertyExists(dataObject.properties[value])">
                    <div class="form-group">
                      <label :for="value">{{value}}</label>
                      <input
                        type="file"
                        class="input-file"
                        :id="value"
                        name="element-assets"
                        accept="image/png, image/jpeg, image/gif, video/mp4, application/pdf, audio/mpeg3, audio/wav"
                        @change="handleFileUpload"
                      />
                    </div>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'events' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="lockInteraction === false">
                    <PageEditorPropertiesEvents :events="dataObject.properties[value]" :properties="optionsSheet.options[value]" :eventTemplate="optionsSheet.options.eventTemplate" :components="components" :dataObject="dataObject" :reload="reload" @onChange="updateProperties"/>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'css_classes' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="lockInteraction === false">
                    <PageEditorPropertiesCssClasses :css_classes="dataObject.properties[value]" :properties="optionsSheet.options[value]" @onChange="updateProperties"/>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'custom_styles' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="lockInteraction === false">
                    <PageEditorPropertiesCustomCss :custom_styles="dataObject.properties[value]" :properties="optionsSheet.options[value]" @onChange="updateProperties"/>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'multiple_choice' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="lockInteraction === false">
                    <PageEditorPropertiesMultipleChoice :multiple_choice="dataObject.properties[value]" :properties="optionsSheet.options[value]" :template="optionsSheet.options.multipleChoiceTemplate" @onChange="updateProperties"/>
                  </div>
                </div>

                <div v-else-if="optionsSheet.options[value]['type'] === 'timer_trigger_events' && authorize(optionsSheet.options[value]['minPermissionLevel'])">
                  <div v-if="lockInteraction === false">
                    <PageEditorPropertiesTimerEvents :timer_trigger_events="dataObject.properties[value]" :components="components" :dataObject="dataObject" @onChange="updateProperties"/>
                  </div>
                </div>

                <div v-if="optionsSheet.options[value]['helper']" class="helper-icon"><span>?</span></div>

                <div v-if="optionsSheet.options[value]['helper']" class="tool-tip">
                  <span>{{ optionsSheet.options[value]['helper_text']}}</span>
                </div>

                <template v-if="authorize(permissibleActions.taggable.minPermissionLevel)">
                  <hr v-if="!optionsSheet.options[value]['custom']"/>
                </template>

              </div>

            </div>
          </div>

        </div>

      </div>

    </div>

    <div>
      <b-modal ref="addChildModal" class="addChildModal" title="Add Child Component To Component" ok-variant="default" ok-title="Add To Component">
        <b-list-group>
          <b-list-group-item href="#" variant="outline-primary" class="card" v-for="component in templateComponents" :key="component.uuid" @click="addChildTemplateComponentAction(component, dataObject)">{{ component.name }}</b-list-group-item>
          <b-list-group-item href="#" variant="outline-primary" class="card" @click="addChildComponentAction(dataObject)">New Blank Component</b-list-group-item>
        </b-list-group>
      </b-modal>
      <b-modal ref="changeParent" class="changeParent" title="Change Parent, Component, or Page" ok-variant="default" ok-title="Force Change" @ok="remapObject()">
        <div v-if="dataObject">
          <h6>Changing {{dataObject.name || `Unnamed ${dataObject.object_model_name}`}} ({{dataObject.object_model_name}})</h6>
          <div v-if="dataObject.object_model_name === 'Component'">
            <label for="new_page_id">Page ID</label>
            <v-select
              id="new_page_id"
              v-model="newPageID"
              :options="remappablePages"
              size="sm"
              class="mt-3"
            ></v-select>
            <label for="new_comp_parent_id">Parent ID</label>
            <v-select
              id="new_comp_parent_id"
              v-model="newParentID"
              :options="remappableComponents"
              size="sm"
              class="mt-3"
            ></v-select>
            <p>
              Page_ID: {{dataObject.page_id}} <br/>
              Parent ID: {{dataObject.parent_id}} <br/>
            </p>
          </div>
          <div v-else>
            <label for="new_component_id">Component ID</label>
            <v-select
              id="new_component_id"
              v-model="newComponentID"
              :options="remappableComponents"
              size="sm"
              class="mt-3"
            ></v-select>
            <label for="new_parent_id">Parent ID</label>
            <v-select
              id="new_parent_id"
              v-model="newParentID"
              :options="remappableElements"
              size="sm"
              class="mt-3"
            ></v-select>
            <p>
              Component ID: {{dataObject.component_id}}
              <br/>
              Parent ID: {{dataObject.parent_id}}
            </p>
          </div>
        </div>
      </b-modal>
      <!--    CUSTOM STYLES MODAL  -->
      <b-modal ref="customStyles" class="customStyles" title="Custom Styles for Component" ok-variant="default">
        <div v-if="dataObject">
          <div v-for="value in optionsSheet.prop[elementType]['allowed_props']" :key="value">
            <div v-if="optionsSheet.options[value] && optionsSheet.options[value]['custom']" class="custom-style-component">
              <div v-if="optionsSheet.options[value]['type'] === 'number'">
                <div v-if="ifPropertyExists(dataObject.properties[value])">
                  <div class="form-group">
                    <label :for="value">{{value}}</label>
                    <input
                      type="number"
                      step="0.01"
                      :min="optionsSheet.options[value]['min']"
                      :max="optionsSheet.options[value]['max']"
                      class="form-control"
                      v-model.number="dataObject.properties[value]"
                      :placeholder="dataObject.properties[value]"
                      :id="value"
                      @change="updateProperties"
                    />
                  </div>
                </div>
              </div>

              <div v-else-if="optionsSheet.options[value]['type'] === 'string'">
                <div class="form-group">
                  <label :for="value">{{value}}</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="dataObject.properties[value]"
                    :placeholder="dataObject.properties[value]"
                    :id="value"
                    @change="updateProperties"
                  />
                </div>
              </div>

              <div v-else-if="optionsSheet.options[value]['type'] === 'text'">
                <div v-if="ifPropertyExists(dataObject.properties[value])">
                  <div class="form-group">
                    <label :for="value">{{value}}</label>
                    <ckeditor :editor="editor" v-model="dataObject.properties[value]" :config="editorConfig" @blur="updateProperties"></ckeditor>
                  </div>
                </div>
              </div>

              <div v-else-if="optionsSheet.options[value]['type'] === 'boolean'">
                <div v-if="ifPropertyExists(dataObject.properties[value])">
                  <div class="form-group">
                    <label :for="value">{{value}}</label>
                    <input
                      type="checkbox"
                      class="form-control"
                      v-model="dataObject.properties[value]"
                      :id="value"
                      :true-value="true"
                      :false-value="false"
                      :checked="dataObject.properties[value]"
                      @change="updateProperties"
                    />
                  </div>
                </div>
              </div>

              <div v-else-if="optionsSheet.options[value]['type'] === 'select'">
                <div v-if="ifPropertyExists(dataObject.properties[value])">
                  <div class="form-group">
                    <label :for="value">{{value}}</label>
                    <b-form-select
                      :id="value"
                      v-model="dataObject.properties[value]"
                      :options="optionsSheet.options[value]['allowed_values']"
                      size="sm"
                      class="mt-3"
                      @change="updateProperties"
                    ></b-form-select>
                  </div>
                </div>
              </div>

              <div v-if="optionsSheet.options[value]['helper']" class="helper-icon"><span>?</span></div>
              <div v-if="optionsSheet.options[value]['helper']" class="tool-tip">
                <span>{{ optionsSheet.options[value]['helper_text']}}</span>
              </div>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import _ from 'lodash';
import { DataPropertiesService } from '@/common/services/data_properties.servicejs';
import { PageService } from '@/common/services/page.service';
import { ComponentService } from '@/common/services/component.service';
import { ElementService } from '@/common/services/element.service';
import PageEditorPropertiesEvents from './page-editor-properties/page-editor-properties-events.vue';
import PageEditorPropertiesTimerEvents from './page-editor-properties/page-editor-properties-timer-events.vue';
import PageEditorPropertiesCssClasses from './page-editor-properties/page-editor-properties-css-classes.vue';
import PageEditorPropertiesCustomCss from './page-editor-properties/page-editor-properties-custom-css.vue';
import EditorEvents from '../mixins/editor-events';
import PropertyOptions from '../mixins/property-options';
import PageEditorPropertiesMultipleChoice from './page-editor-properties/page-editor-properties-multiple-choice.vue';
import AllSounds from '@/common/kiosk_renderer/sounds/sounds';
import { usePageEditorStore } from '@/stores/PageEditorStore';

export default {
  name: 'PageEditorProperties',
  mixins: [EditorEvents, AllSounds, PropertyOptions],
  components: {
    PageEditorPropertiesEvents,
    PageEditorPropertiesTimerEvents,
    PageEditorPropertiesCssClasses,
    PageEditorPropertiesCustomCss,
    PageEditorPropertiesMultipleChoice,
  },
  props: {
    focus: Object,
    reload: Boolean,
    components: Array,
    templateComponents: Array,
    lockInteraction: Boolean,
    existingSelectItems: Array,
  },
  setup() {
    const pageEditorStore = usePageEditorStore();
    return {
      pageEditorStore,
    };
  },
  data() {
    return {
      kioskId: null,
      dataObject: null,
      elementType: null,
      tags: [],
      selectList: [],
      correctAnswer: '',
      keyboard: '',
      keyboards: null,
      pages: null,
      newPageID: null,
      newParentID: null,
      newComponentID: null,
      remappableComponents: null,
      remappableElements: null,
      remappablePages: null,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: {
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            // 'link',
            'bulletedList',
            'numberedList',
            'blockQuote',
            'undo',
            'redo',
          ],
        },
        language: 'en',
      },
      allSounds: null,
      existingTags: {},
      optionsSheet: {},
    };
  },
  computed: {
    imagePlaceholderOptions() {
      let image = null;
      if (Vue.prototype.$elementMap.length > 1) {
        image = Vue.prototype.$elementMap
          .filter(element => element.properties.element_type === 'image')
          .map(element => ({ value: element.uuid, text: element.name }));
      }
      return image;
    },
    keyboardOptions() {
      let keyboard = null;
      if (Vue.prototype.$elementMap.length > 1) {
        keyboard = Vue.prototype.$elementMap
          .filter(element => element.properties.element_type === 'keyboard')
          .map(element => ({ value: element, text: element.name }));
      }
      return keyboard;
    },
    isDisabled() {
      return this.lockInteraction;
    },
    /**
     * Can this dataObject have a child element?
     */
    allowAddChildElement() {
      let result = true;
      if (this.dataObject) {
        if (this.dataObject.properties) {
          if (this.dataObject.object_model_name === 'Component') {
            if (this.dataObject.properties.element_type === 'carousel') {
              result = false;
            }
          } else if (this.dataObject.object_model_name === 'Element') {
            if (this.optionsSheet.options.allow_nested_elements === false) {
              result = false;
            }
          }
        }
      }
      return result;
    },
    /**
     * Can this dataObject have a child component?
     */
    allowAddChildComponent() {
      let result = true;
      if (this.dataObject) {
        if (this.dataObject.properties) {
          if (this.dataObject.object_model_name === 'Component') {
            if (this.optionsSheet.options.allow_nested_components === false) {
              result = false;
            }
          } else if (this.dataObject.object_model_name === 'Element') {
            result = false;
          }
        }
      }
      return result;
    },
  },
  watch: {
    focus(newFocus) {
      this.elementType = null;
      // So the reason for these strange mappings is you cannot interact in the interface with
      // globals
      this.existingTags = Vue.prototype.$existingTags;
      this.log('focus', 'focus', { existingTags: this.existingTags, permissionsLevel: this.pageEditorStore.getPermissionLevel });
      this.setNewProperties(newFocus);
    },
  },
  created() {
    this.kioskId = this.$route.params.kioskId;
    PageService.get(this.kioskId)
      .then((pages) => {
        this.pages = pages.data;
        this.permissionLevel = this.pageEditorStore.getPermissionLevel;
      });

    // Global Events
    this.$eventHub.$on('updateProperties', this.updateProperties);
  },
  mounted() {
    this.allSounds = this.getAllSounds();
    this.remappablePages = this.mapAllPages(false, true);
    this.remappableComponents = this.mapComponents(null, false, true);
    this.remappableElements = this.mapAllElements(null, false, true);
    this.keyboards = this.keyboardOptions;
  },
  beforeDestroy() {
    this.$eventHub.$off('updateProperties');
  },
  methods: {
    showRemapModal() {
      if (this.dataObject.page_id) {
        this.newPageID = this.findFirstObjectInArrayByProp(this.remappablePages, 'value', this.dataObject.page_id);
      } else {
        this.newPageID = null;
      }
      if (this.dataObject.component_id) {
        this.newComponentID = this.findFirstObjectInArrayByProp(this.remappableComponents, 'value', this.dataObject.component_id);
      } else {
        this.newComponentID = null;
      }
      if (this.dataObject.parent_id) {
        if (this.isObjectAComponent(this.dataObject)) {
          this.newParentID = this.findFirstObjectInArrayByProp(this.remappableComponents, 'value', this.dataObject.parent_id);
        } else {
          this.newParentID = this.findFirstObjectInArrayByProp(this.remappableElements, 'value', this.dataObject.parent_id);
        }
      } else {
        this.newParentID = null;
      }
      this.$refs.changeParent.show();
    },
    showCustomStylesModal() {
      this.$refs.customStyles.show();
    },
    /**
     * Load new properties into the sheet
     */
    setNewProperties(object) {
      if (object) {
        if (this.$refs.tagInput) {
          this.$refs.tagInput.input = '';
        }
        this.tags = [];
        this.selectList = [];
        this.dataObject = object;
        if (object.properties) {
          this.optionsSheet = this.loadPropertyOptions(this.dataObject);
          this.elementType = object.properties.element_type;
          this.tags = object.tags;
          this.selectList = this.existingSelectItems;
          this.correctAnswer = this.dataObject.properties.correct_answer;
          this.keyboard = this.dataObject.properties.keyboard_select;
        }
      }
    },
    /**
     * If the property exists, display it
     */
    ifPropertyExists(property) {
      let result = false;
      if (property || property === 0 || property === false || property === '') {
        result = true;
      }
      return result;
    },
    /**
     * Update the properties
     */
    // updateProperties: _.debounce(function debounceSave() {
    //   if (this.lockInteraction) {
    //     return;
    //   }
    //   this.dataObject.tags = this.tags;
    //   this.sendNormalizationAction(this.dataObject);
    // }, 250),
    updateProperties() {
      this.log('updateProperties', 'dataObject', this.dataObject, true);
      if (this.lockInteraction) {
        return;
      }
      this.dataObject.tags = this.tags;
      this.sendNormalizationAction(this.dataObject);
    },
    lockOrUnlockChildren: _.debounce(function debounceLockOrUnlock() {
      this.lockOrUnlockAllChildrenAction(this.dataObject, !this.dataObject.properties.locked);
    }, 250),
    /**
     * As the name suggestions, display add child modal which is almost identical to the one in
     * the editor.vue for adding an item
     */
    displayAddChildModal() {
      this.$refs.addChildModal.show();
    },
    /**
     * When a tag is added, add it to the dataObjects tags
     */
    onTagAdded(newTag) {
      if (this.dataObject.tags.indexOf(newTag) < 0) {
        this.dataObject.tags.push(newTag);
        this.updateProperties();
      }
    },
    /**
     * When a tag is removed, remove it from the dataObject's tags
     */
    onTagRemoved() {
      const result = _.difference(this.dataObject.tags, this.tags);
      if (result.length > 0) {
        this.dataObject.tags = this.tags;
        this.updateProperties();
      }
    },
    onSelectItemAdded() {
      // eslint-disable-next-line
      const result = _.difference(this.dataObject.properties['select_list_items'], this.existingSelectItems);
      if (result.length > 0) {
        this.$emit('updateExistingSelectItems', result);
        this.updateProperties();
      }
    },
    onSelectItemRemoved() {
      // eslint-disable-next-line
      this.dataObject.properties['select_list_items'] = this.selectList;
      // eslint-disable-next-line
      this.$emit('updateExistingSelectItems', this.dataObject.properties['select_list_items']);
      this.updateProperties();
    },
    onCorrectAnswerSelection(val) {
      this.dataObject.properties.correct_answer = val.target.value;
      this.updateProperties();
    },
    keyboardSelection(val) {
      this.dataObject.properties.keyboard_select = val.target.value;
      this.updateProperties();
    },
    /**
     * Upload file to dataObject
     * @param e
     */
    handleFileUpload(e) {
      const [file] = e.target.files;

      if (this.dataObject && file) {
        this.sendLockInteraction();
        const formData = new FormData();
        // formData.set('element[name]', this.dataObject.name);
        formData.set(`${this.dataObject.object_model_name.toLowerCase()}[asset]`, file);

        DataPropertiesService.upload(this.dataObject, this.kioskId, formData)
          .then(({ data }) => {
            if (data) {
              this.dataObject = _.cloneDeep(data);
              if (this.dataObject.name) {
                this.$notify({
                  title: 'Uploaded image',
                  text: `Successfully uploaded an image for ${this.dataObject.name}!`,
                  type: 'success',
                  width: 350,
                });
                this.updateObject(this.dataObject);
              } else {
                this.dataObject.name = file.name;
                this.sendNormalizationAction(this.dataObject);
              }
            }
          })
          .catch((err) => {
            this.sendUnlockInteraction();
            console.error(err);
            this.$notify({
              title: 'Could not submit form!',
              text: err.response.data.error,
              type: 'error',
              width: 350,
            });
          });
      }
    },
    remapObject() {
      const newPageID = (this.newPageID) ? this.newPageID.value : null;
      const newComponentID = (this.newComponentID) ? this.newComponentID.value : null;
      const newParentID = (this.newParentID) ? this.newParentID.value : null;
      this.sendLockInteraction();
      this.$refs.changeParent.hide();
      if (this.isObjectAComponent(this.dataObject)) {
        this.dataObject.page_id = newPageID;
        this.dataObject.parent_id = newParentID;
        ComponentService.update(this.kioskId, this.dataObject.id, this.dataObject)
          .then(() => {
            this.forceReloadPage();
          });
      } else {
        const oldComponentId = this.dataObject.component_id;
        this.dataObject.component_id = newComponentID;
        this.dataObject.parent_id = newParentID;
        ElementService.update(this.kioskId, oldComponentId, this.dataObject.id, this.dataObject)
          .then(() => {
            if (oldComponentId !== newComponentID && this.dataObject.children) {
              this.remapElementComponentDF(this.dataObject.children, newComponentID);
            }
            this.forceReloadPage();
          });
      }
    },
    remapElementComponentDF(elements, componentId) {
      if (elements) {
        for (let i = 0; i < elements.length; i += 1) {
          const element = elements[i];
          const oldComponentId = element.component_id;
          element.component_id = componentId;
          // Start looking through child
          if (element.children && element.children.length > 0) {
            this.remapElementComponentDF(element.children, componentId);
          }
          ElementService.update(this.kioskId, oldComponentId, element.id, element);
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import "../styles/vue-tagsinput";

.page-editor-properties-toolbar {
  text-align: left;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 500px;
  max-height: 750px;
  margin: 0;
  padding: 25px 30px 25px 15px;
  font-size: 10px;
}
.tool-tip-relative {
  position: relative;
}

.helper-icon {
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 100%;
  border: 1px solid black;
  height: 15px;
  width: 15px;
  text-align: center;
  font-size: 10px;
}

.helper-icon:hover + .tool-tip {
  display: block;
}

.tool-tip {
  display: none;
  position: absolute;
  top: 25px;
  z-index: 9;
  background: white;
  border: 1px solid black;
  border-radius: 5px;
  padding: 10px;
}

.custom-styles {
  margin-top: 10px;
  button {
    width: 100%;
    font-size: 10px;
  }
}

.btn {
  font-size: 10px;
}

.custom-style-component {
  position: relative;
}
</style>
